import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cx } from "../cx.ts";
import type { Tone } from "./tone.ts";
import { TONE_SOFT } from "./tone.ts";

interface ChipBase {
  tone?: Tone;
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
}

export interface ChipProps
  extends ChipBase,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "className"> {
  /** A toggle chip (preset pickers, filters). Rendered as a button with aria-pressed. */
  selected?: boolean;
}

const SELECTED =
  "border-brand bg-brand text-on-brand sun:border-black sun:bg-active sun:text-on-active";

/**
 * A pill. With `selected` (true or false) it is a toggle button for preset
 * pickers and filters; without it, a static label such as a source chip.
 */
export function Chip({
  tone = "neutral",
  icon,
  className,
  children,
  selected,
  type = "button",
  ...rest
}: ChipProps) {
  const shape = cx(
    "inline-flex items-center gap-1.5 rounded-full border px-3 font-medium text-sm",
    "sun:border-2 sun:border-black sun:text-min",
    className,
  );
  if (selected === undefined) {
    return (
      <span className={cx(shape, "min-h-7 py-0.5", TONE_SOFT[tone])}>
        {icon}
        {children}
      </span>
    );
  }
  return (
    <button
      type={type}
      aria-pressed={selected}
      className={cx(
        shape,
        "min-h-target transition-colors",
        selected ? SELECTED : "border-line-strong bg-surface text-ink hover:bg-surface-muted",
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}
